// Product management functions
// Products are stored in the admin collection as a subcollection
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

// Products are stored as a subcollection under admin/settings
// Path structure: admin/settings/products (3 segments = valid collection path)
const ADMIN_SETTINGS_DOC = 'admin/settings'
const PRODUCTS_COLLECTION = 'products'

/**
 * Create a new product
 * @param {Object} productData - Product data (name, description, url)
 * @returns {Promise<string>} Document ID
 */
export const createProduct = async (productData) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
    const docRef = await addDoc(productsRef, {
      ...productData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating product:', error)
    throw error
  }
}

/**
 * Update an existing product
 * @param {string} productId - Product document ID
 * @param {Object} updates - Fields to update
 */
export const updateProduct = async (productId, updates) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
    const docRef = doc(productsRef, productId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating product:', error)
    throw error
  }
}

/**
 * Delete a product
 * @param {string} productId - Product document ID
 */
export const deleteProduct = async (productId) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
    const docRef = doc(productsRef, productId)
    await deleteDoc(docRef)
  } catch (error) {
    console.error('Error deleting product:', error)
    throw error
  }
}

/**
 * Get a single product by ID
 * @param {string} productId - Product document ID
 * @returns {Promise<Object|null>} Product data
 */
export const getProduct = async (productId) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
    const docRef = doc(productsRef, productId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting product:', error)
    throw error
  }
}

/**
 * Get all products
 * @returns {Promise<Array>} Array of products
 */
export const getProducts = async () => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
    const q = query(
      productsRef,
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting products:', error)
    throw error
  }
}

/**
 * Subscribe to products changes (real-time)
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToProducts = (callback) => {
  const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
  const productsRef = collection(adminSettingsRef, PRODUCTS_COLLECTION)
  const q = query(
    productsRef,
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(products)
  }, (error) => {
    console.error('Error subscribing to products:', error)
  })
}
